/**
 * biffTemplate — write a Bizlink batch INTO the bank's own .xls template,
 * changing nothing but the cells.
 *
 * WHY NOT BUILD THE FILE. Bizlink's upload is strict, and the first file the
 * app built from scratch (bizlinkSheet.ts, via SheetJS's BIFF8 writer) was
 * refused. Set beside the template Excel saves, it differs in everything a
 * strict reader might key on: one font instead of Excel's twenty-five, no
 * style table, inline strings instead of a shared string table, no
 * document-property streams, a different compound-file root. Nobody outside
 * the bank knows which of those it checks, so the safe move is to not
 * differ: take the template file the clerk already uploads successfully,
 * keep every record in it, and replace only the rows.
 *
 * WHAT IS KEPT AND WHAT IS REPLACED. The workbook stream is a sequence of
 * BIFF records. Everything in the globals (fonts, formats, cell styles, the
 * sheet list, printer settings, names) is copied byte for byte, except the
 * shared string table, which is rebuilt for our strings, and the drawing
 * group behind the template's cell comments, which goes with them. In the
 * sheet, the ROW and cell records are regenerated — each of our cells takes
 * the style index the template used at that position (rows 0 and 1 as they
 * are; every detail row styled like the template's first detail row) — and
 * the INDEX/DBCELL offset tables and the comments are dropped, since Excel
 * rebuilds the first and nobody uploads the second. The sheet's own settings
 * (page setup, margins, column widths, window, selection) are untouched.
 *
 * ACCOUNT NUMBERS AS THE TEMPLATE HAS THEM: a number, unless it starts with
 * a zero, in which case text — both real template files do exactly this.
 *
 * NO STYLE TABLE IS INVENTED HERE; if the template has no cell at a
 * position we write, the default cell style (15) is used. Pure apart from
 * SheetJS's compound-file reader/writer.
 */
import { CFB } from "xlsx";
import type { IBizlinkBatch } from "./bizlink";
import { isoToExcelSerial, timeToExcelFraction } from "./bizlinkSheet";

// Record types (MS-XLS).
const R = {
  BOF: 0x0809, EOF: 0x000a, BOUNDSHEET: 0x0085, SST: 0x00fc, EXTSST: 0x00ff, CONTINUE: 0x003c,
  MSODRAWINGGROUP: 0x00eb, MSODRAWING: 0x00ec, OBJ: 0x005d, TXO: 0x01b6, NOTE: 0x001c,
  INDEX: 0x020b, DBCELL: 0x00d7, DIMENSIONS: 0x0200, ROW: 0x0208,
  LABELSST: 0x00fd, LABEL: 0x0204, RK: 0x027e, NUMBER: 0x0203, MULRK: 0x00bd,
  BLANK: 0x0201, MULBLANK: 0x00be, BOOLERR: 0x0205, FORMULA: 0x0006, STRING: 0x0207, RSTRING: 0x00d6,
} as const;

const CELL_TYPES = new Set<number>([R.LABELSST, R.LABEL, R.RK, R.NUMBER, R.MULRK, R.BLANK, R.MULBLANK, R.BOOLERR, R.FORMULA, R.STRING, R.RSTRING]);
const DROP_IN_SHEET = new Set<number>([R.INDEX, R.DBCELL, R.MSODRAWING, R.OBJ, R.TXO, R.NOTE]);

/** The largest data a single BIFF record may carry. */
const MAX_RECORD = 8224;
/** Excel's default cell XF, when the template has no cell to copy from. */
const DEFAULT_XF = 15;

interface IRecord { type: number; data: Uint8Array }

function u16(b: Uint8Array, i: number): number { return b[i] | (b[i + 1] << 8); }
function u32(b: Uint8Array, i: number): number { return (b[i] | (b[i + 1] << 8) | (b[i + 2] << 16)) + b[i + 3] * 0x1000000; }

export function parseRecords(stream: Uint8Array): IRecord[] {
  const out: IRecord[] = [];
  let i = 0;
  while (i + 4 <= stream.length) {
    const type = u16(stream, i), len = u16(stream, i + 2);
    out.push({ type, data: stream.subarray(i + 4, i + 4 + len) });
    i += 4 + len;
  }
  return out;
}

function rec(type: number, data: Uint8Array): IRecord { return { type, data }; }

class W {
  private parts: number[] = [];
  u8(v: number): this { this.parts.push(v & 0xff); return this; }
  u16(v: number): this { return this.u8(v).u8(v >>> 8); }
  u32(v: number): this { return this.u16(v & 0xffff).u16(v >>> 16); }
  f64(v: number): this {
    const dv = new DataView(new ArrayBuffer(8)); dv.setFloat64(0, v, true);
    for (let i = 0; i < 8; i++) this.u8(dv.getUint8(i));
    return this;
  }
  bytes(b: ArrayLike<number>): this { for (let i = 0; i < b.length; i++) this.u8(b[i]); return this; }
  out(): Uint8Array { return Uint8Array.from(this.parts); }
}

/** XLUnicodeRichExtendedString without rich/ext runs: cch, flags, chars. */
function encodeString(s: string): Uint8Array {
  const wide = [...s].some(ch => ch.charCodeAt(0) > 0xff);
  const w = new W().u16(s.length).u8(wide ? 1 : 0);
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (wide) w.u16(c); else w.u8(c);
  }
  return w.out();
}

/** A cell value to write. Strings become shared strings; numbers NUMBER records. */
export type CellValue = string | number;

export interface ICellSpec { row: number; col: number; value: CellValue }

/** Numeric unless a leading zero would be lost — the template's own rule. */
export function accountCell(account: string): CellValue {
  const s = account.trim();
  return /^[1-9]\d{0,14}$/.test(s) ? Number(s) : s;
}

/** The cells of a batch, laid out as the FORMULA sheet lays them out. */
export function batchCells(batch: IBizlinkBatch): ICellSpec[] {
  const cells: ICellSpec[] = [
    { row: 0, col: 0, value: "H" }, { row: 0, col: 1, value: "Payroll Date" }, { row: 0, col: 2, value: isoToExcelSerial(batch.payrollDate) },
    { row: 0, col: 3, value: "Payroll Time" }, { row: 0, col: 4, value: timeToExcelFraction(batch.payrollTime) },
    { row: 0, col: 5, value: "Total Amount" }, { row: 0, col: 6, value: batch.totalAmount },
    { row: 0, col: 7, value: "Total Count" }, { row: 0, col: 8, value: batch.totalCount },
    { row: 0, col: 9, value: "FUNDING ACCOUNT" }, { row: 0, col: 10, value: batch.fundingAccount },
  ];
  ["DETAIL CONSTANT", "EMPLOYEE NAME", "EMPLOYEE ACCOUNT", "AMOUNT", "REMARKS"].forEach((h, c) => cells.push({ row: 1, col: c, value: h }));
  batch.rows.forEach((r, i) => {
    const row = i + 2;
    cells.push(
      { row, col: 0, value: r.constant }, { row, col: 1, value: r.employeeName }, { row, col: 2, value: accountCell(r.employeeAccount) },
      { row, col: 3, value: r.amount }, { row, col: 4, value: r.remarks },
    );
  });
  return cells;
}

/** Style index per template cell, and the first detail row's per-column styles. */
interface ITemplateStyles { at: Map<string, number>; detailRow: Map<number, number>; rowRecords: Map<number, Uint8Array> }

function templateStyles(sheet: readonly IRecord[]): ITemplateStyles {
  const at = new Map<string, number>();
  const rowRecords = new Map<number, Uint8Array>();
  for (const r of sheet) {
    const d = r.data;
    if (r.type === R.ROW) { rowRecords.set(u16(d, 0), d); continue; }
    if (!CELL_TYPES.has(r.type)) continue;
    const rw = u16(d, 0), col = u16(d, 2);
    if (r.type === R.MULBLANK || r.type === R.MULRK) {
      const last = u16(d, d.length - 2);
      const stride = r.type === R.MULBLANK ? 2 : 6;
      for (let c = col, k = 4; c <= last; c++, k += stride) at.set(`${rw}:${c}`, u16(d, k));
    } else {
      at.set(`${rw}:${col}`, u16(d, 4));
    }
  }
  const detailRow = new Map<number, number>();
  for (let c = 0; c < 11; c++) { const xf = at.get(`2:${c}`); if (xf !== undefined) detailRow.set(c, xf); }
  return { at, detailRow, rowRecords };
}

function styleFor(t: ITemplateStyles, row: number, col: number): number {
  if (row <= 1) return t.at.get(`${row}:${col}`) ?? DEFAULT_XF;
  return t.detailRow.get(col) ?? t.at.get(`2:${col}`) ?? DEFAULT_XF;
}

/** The ROW record for a row: rows 0 and 1 as the template has them; every
 *  detail row a copy of the template's first detail row, renumbered. */
function rowRecord(t: ITemplateStyles, row: number, colMac: number): Uint8Array {
  const src = t.rowRecords.get(row <= 1 ? row : 2) ?? t.rowRecords.get(0);
  const d = src ? Uint8Array.from(src) : new W().u16(0).u16(0).u16(0).u16(0x0120).u16(0).u16(0).u16(0x0100).u16(0x000f).out();
  d[0] = row & 0xff; d[1] = row >>> 8;
  d[2] = 0; d[3] = 0;
  d[4] = colMac & 0xff; d[5] = colMac >>> 8;
  return d;
}

/**
 * The template with the batch written into it. Throws when the template is
 * not a single-sheet BIFF8 workbook, or the batch's strings will not fit one
 * shared-string record (a few hundred payees — split the batch).
 */
export function writeBatchIntoTemplate(template: Uint8Array, batch: IBizlinkBatch): Uint8Array {
  const cfb = CFB.read(template, { type: "array" });
  const entry = CFB.find(cfb, "/Workbook") ?? CFB.find(cfb, "/Book");
  if (!entry) throw new Error("That file has no Excel workbook stream — it is not an .xls template.");
  const stream: Uint8Array = entry.content instanceof Uint8Array ? entry.content : Uint8Array.from(entry.content as ArrayLike<number>);
  const all = parseRecords(stream);

  // Substreams: globals up to the first EOF; the first sheet from its BOF.
  const firstEof = all.findIndex(r => r.type === R.EOF);
  if (firstEof < 0 || all[0]?.type !== R.BOF) throw new Error("Not a BIFF workbook: no globals substream.");
  const globals = all.slice(0, firstEof + 1);
  const sheetStart = firstEof + 1;
  if (all[sheetStart]?.type !== R.BOF) throw new Error("The template has no worksheet after its globals.");
  let sheetEnd = sheetStart;
  while (sheetEnd < all.length && all[sheetEnd].type !== R.EOF) sheetEnd++;
  const sheet = all.slice(sheetStart, sheetEnd + 1);
  const boundsheets = globals.filter(r => r.type === R.BOUNDSHEET);
  if (boundsheets.length !== 1) throw new Error(`The template must have exactly one sheet; this one has ${boundsheets.length}.`);

  const styles = templateStyles(sheet);
  const cells = batchCells(batch);

  // Shared strings, deduplicated in first-use order.
  const sst: string[] = [];
  const sstIndex = new Map<string, number>();
  let total = 0;
  for (const c of cells) {
    if (typeof c.value !== "string") continue;
    total++;
    if (!sstIndex.has(c.value)) { sstIndex.set(c.value, sst.length); sst.push(c.value); }
  }
  const sstBody = new W().u32(total).u32(sst.length);
  const stringOffsets: number[] = [];
  let sstLen = 8;
  for (const s of sst) {
    const enc = encodeString(s);
    stringOffsets.push(sstLen);
    sstBody.bytes(enc);
    sstLen += enc.length;
  }
  const sstData = sstBody.out();
  if (sstData.length > MAX_RECORD) {
    throw new Error(`Too many payees for one bank file (${batch.rows.length}) — split the batch by schedule group.`);
  }

  // New sheet records.
  const lastRow = cells.reduce((m, c) => Math.max(m, c.row), 0);
  const colMac = 11;
  const newSheet: IRecord[] = [];
  let rowsEmitted = false, cellsEmitted = false, prevDropped = false;
  for (const r of sheet) {
    if (r.type === R.CONTINUE && prevDropped) continue;
    prevDropped = false;
    if (DROP_IN_SHEET.has(r.type)) { prevDropped = true; continue; }
    if (r.type === R.DIMENSIONS) {
      newSheet.push(rec(R.DIMENSIONS, new W().u32(0).u32(lastRow + 1).u16(0).u16(colMac).u16(0).out()));
      continue;
    }
    if (r.type === R.ROW) {
      if (!rowsEmitted) {
        for (let rw = 0; rw <= lastRow; rw++) newSheet.push(rec(R.ROW, rowRecord(styles, rw, colMac)));
        rowsEmitted = true;
      }
      continue;
    }
    if (CELL_TYPES.has(r.type)) {
      // A template written without ROW records (some generators skip them)
      // still gets ours, ahead of the first cell.
      if (!rowsEmitted) {
        for (let rw = 0; rw <= lastRow; rw++) newSheet.push(rec(R.ROW, rowRecord(styles, rw, colMac)));
        rowsEmitted = true;
      }
      if (!cellsEmitted) {
        for (const c of cells) {
          const xf = styleFor(styles, c.row, c.col);
          if (typeof c.value === "string") {
            newSheet.push(rec(R.LABELSST, new W().u16(c.row).u16(c.col).u16(xf).u32(sstIndex.get(c.value)!).out()));
          } else {
            newSheet.push(rec(R.NUMBER, new W().u16(c.row).u16(c.col).u16(xf).f64(c.value).out()));
          }
        }
        cellsEmitted = true;
      }
      continue;
    }
    newSheet.push(r);
  }
  if (!rowsEmitted || !cellsEmitted) throw new Error("The template's sheet has no rows to model the batch on.");

  // New globals: SST and EXTSST rebuilt, the comments' drawing group dropped,
  // the sheet offset patched once the globals' length is known.
  const newGlobals: IRecord[] = [];
  prevDropped = false;
  for (const r of globals) {
    if (r.type === R.CONTINUE && prevDropped) continue;
    prevDropped = false;
    if (r.type === R.MSODRAWINGGROUP) { prevDropped = true; continue; }
    if (r.type === R.SST) { newGlobals.push(rec(R.SST, sstData)); continue; }
    if (r.type === R.EXTSST) { newGlobals.push(rec(R.EXTSST, new Uint8Array(0))); continue; } // filled below
    newGlobals.push(r.type === R.BOUNDSHEET ? rec(R.BOUNDSHEET, Uint8Array.from(r.data)) : r);
  }
  // Absolute offsets: walk the globals to find where the SST record lands.
  let pos = 0, sstPos = -1;
  for (const r of newGlobals) { if (r.type === R.SST) sstPos = pos; pos += 4 + r.data.length; }
  const extsst = new W().u16(8);
  for (let i = 0; i < sst.length; i += 8) extsst.u32(sstPos + 4 + stringOffsets[i]).u16(4 + stringOffsets[i]).u16(0);
  const globalsLen = newGlobals.reduce((s, r) => s + 4 + (r.type === R.EXTSST ? extsst.out().length : r.data.length), 0);
  for (const r of newGlobals) {
    if (r.type === R.EXTSST) r.data = extsst.out();
    if (r.type === R.BOUNDSHEET) { const d = r.data; d[0] = globalsLen & 0xff; d[1] = (globalsLen >>> 8) & 0xff; d[2] = (globalsLen >>> 16) & 0xff; d[3] = (globalsLen >>> 24) & 0xff; }
  }

  const out = new W();
  for (const r of [...newGlobals, ...newSheet]) { out.u16(r.type).u16(r.data.length).bytes(r.data); }
  const newStream = out.out();

  // The stream is replaced in place; the container's other streams
  // (SummaryInformation, DocumentSummaryInformation, CompObj) go back out as
  // they came in. SheetJS's container writer does add its own 4-byte marker
  // stream ("Sh33tJ5") on every write — Excel and every reader tried
  // ignore unknown streams; should Bizlink not, the fix is a container
  // writer of our own, not a change to the workbook stream built here.
  entry.content = newStream;
  entry.size = newStream.length;
  const written = CFB.write(cfb, { type: "array" }) as ArrayLike<number>;
  return new Uint8Array(written);
}

/** Settings key under which the uploaded template is stored, base64. */
export const BIZLINK_TEMPLATE_KEY = "bizlinkTemplate";
/** A template is ~34 KB; anything past this is not the FORMULA sheet saved as .xls. */
export const BIZLINK_TEMPLATE_MAX_BYTES = 200 * 1024;

export function bytesToBase64(bytes: Uint8Array): string {
  if (typeof btoa === "function") {
    let s = "";
    for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(s);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any).Buffer.from(bytes).toString("base64");
}

export function base64ToBytes(b64: string): Uint8Array {
  if (typeof atob === "function") {
    const s = atob(b64);
    const out = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
    return out;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return new Uint8Array((globalThis as any).Buffer.from(b64, "base64"));
}

/** Cheap sanity check for an uploaded template before it is stored. */
export function describeTemplate(bytes: Uint8Array): { sheets: number; records: number; strings: number } {
  const cfb = CFB.read(bytes, { type: "array" });
  const entry = CFB.find(cfb, "/Workbook") ?? CFB.find(cfb, "/Book");
  if (!entry) throw new Error("Not an .xls workbook (no Workbook stream).");
  const all = parseRecords(entry.content instanceof Uint8Array ? entry.content : Uint8Array.from(entry.content as ArrayLike<number>));
  const sstRec = all.find(r => r.type === R.SST);
  return {
    sheets: all.filter(r => r.type === R.BOUNDSHEET).length,
    records: all.length,
    strings: sstRec ? u32(sstRec.data, 4) : 0,
  };
}
